import { useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import type { PatchData } from '@api/onboarding'
import api from '@api/onboarding'
import useUserInfo from '@helpers/useUserInfo'

import { useStatefulMutation } from '../mutation'
import { queryClient } from '../queryClient'
import { useRoles } from './roles'

const baseKey = 'onboarding'

type Options = {
  placeholderData?: any
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
}

export const useOnboarding = (options?: Options) => {
  const { accountId: id } = useUserInfo()
  const { role } = useRoles()

  const queryKey = useMemo(() => [baseKey, id, role], [id, role])
  const saveKey = useMemo(() => [...queryKey, 'save'], [queryKey])
  const enabled = !!id && !!role

  const query = useQuery({
    queryKey: queryKey,
    // `enabled` below already gates this, but TS can't see that through the
    // option — so re-narrow rather than returning `false`, which would widen
    // `data` for every consumer.
    queryFn: () => (id && role ? api.load({ id, role }) : null),
    enabled,
    placeholderData: null,
    refetchOnMount: true,
    ...options,
  })

  const invalidateQuery = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey })
  }, [queryKey])

  const saveMutation = useStatefulMutation(
    async (data: PatchData) => enabled && api.save({ id, role, data }),
    {
      mutationKey: saveKey,
      onSuccess: invalidateQuery,
    }
  )

  return {
    queryKey,
    invalidateQuery,
    ...query,

    saveOnboarding: saveMutation.mutateAsync,
    saveOnboardingState: saveMutation.state,
    saveKey,
  }
}
